import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { getRequest, showMessage } from "../../../../config";

const OBJECTIVE_STYLES = {
    Investment: { bg: "#e3f2fd", color: "#1976d2" },
    Exit: { bg: "#fce4ec", color: "#c62828" },
    Growth: { bg: "#e8f5e9", color: "#388e3c" },
    All: { bg: "#f1f3f5", color: "#495057" }
};

const SUBSCRIPTION_STYLES = {
    Free: { bg: "#e8f5e9", color: "#388e3c" },
    Lite: { bg: "#fff3e0", color: "#f57c00" },
    Pro: { bg: "#e3f2fd", color: "#1976d2" },
    "Pro Portfolio": { bg: "#f3e5f5", color: "#7b1fa2" },
    All: { bg: "#f1f3f5", color: "#495057" }
};

const badgeStyle = (palette) => ({
    backgroundColor: palette?.bg || "#f5f5f5",
    color: palette?.color || "#757575",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "0.75rem",
    fontWeight: 600
});

const statCardStyle = {
    background: "linear-gradient(180deg, #ffffff 0%, #f8faff 100%)",
    border: "1px solid #e8edff",
    borderRadius: "12px",
    padding: "0.85rem 1rem",
    boxShadow: "0 4px 14px rgba(74, 103, 255, 0.08)"
};

const countQuestionsInSubFolders = (subFolders) => {
    if (!subFolders || typeof subFolders !== "object") return 0;

    return Object.values(subFolders).reduce((acc, subFolder) => {
        const ownQuestion = subFolder?.question ? 1 : 0;
        const extraQuestions = Array.isArray(subFolder?.questions) ? subFolder.questions.length : 0;
        const nestedCount = countQuestionsInSubFolders(subFolder?.subFolders);
        return acc + ownQuestion + extraQuestions + nestedCount;
    }, 0);
};

const countQuestionsInSection = (section) => {
    const mainQuestion = section?.question ? 1 : 0;
    return mainQuestion + countQuestionsInSubFolders(section?.subFolders);
};

const renderQuestionCard = (question, fallbackNo) => {
    if (!question) return null;

    const optionCount = Array.isArray(question?.options) ? question.options.length : 0;

    return (
        <div
            key={question._id || fallbackNo}
            className="bg1 br10px bss bw1px bo5 p0-75rem"
            style={{ marginTop: "0.75rem", paddingTop: "0.65rem", paddingBottom: "0.65rem" }}
        >
            <div className="df aic jcsb" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
                <span className="fw600 c15 fs0-85rem">{question.qNo || fallbackNo || "-"}</span>
                <div className="df aic" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
                    {question.answerType && (
                        <span className="fs0-7rem fw600" style={badgeStyle({ bg: "#edf2ff", color: "#3b5bdb" })}>
                            {question.answerType}
                        </span>
                    )}
                    <span className="fs0-7rem fw600" style={badgeStyle({ bg: "#f8f9fa", color: "#495057" })}>
                        Options: {optionCount}
                    </span>
                </div>
            </div>
            <p className="m0 mt0-5rem c15 fs0-85rem" style={{ lineHeight: 1.5 }}>{question.qDescription || "-"}</p>
        </div>
    );
};

const renderSubFolders = (subFolders, level = 1) => {
    if (!subFolders || typeof subFolders !== "object" || Object.keys(subFolders).length === 0) {
        return null;
    }

    const sortedKeys = Object.keys(subFolders).sort((a, b) => Number(a) - Number(b));

    return (
        <div
            className="df fdc"
            style={{
                gap: "0.75rem",
                marginTop: "0.75rem",
                paddingLeft: level > 1 ? "0.75rem" : 0,
                borderLeft: level > 1 ? "2px dashed #edf1ff" : "none"
            }}
        >
            {sortedKeys.map((key) => {
                const sf = subFolders[key];
                const hasNested = sf?.subFolders && typeof sf.subFolders === "object" && Object.keys(sf.subFolders).length > 0;
                return (
                    <div
                        key={sf?.no || key}
                        className="bg1 br10px bss bw1px bo5 p0-75rem"
                        style={{ paddingTop: "0.7rem", paddingBottom: "0.7rem" }}
                    >
                        <div className="df aic jcsb" style={{ gap: "0.75rem", flexWrap: "wrap", marginBottom: "0.15rem" }}>
                            <span className="fw600 c15 fs0-8rem" style={{ lineHeight: 1.4 }}>
                                {sf?.no || "-"} - {sf?.name || "Sub section"}
                            </span>
                        </div>
                        {renderQuestionCard(sf?.question, sf?.no)}
                        {Array.isArray(sf?.questions) &&
                            sf.questions.map((q) => renderQuestionCard(q, q?.qNo || sf?.no))}
                        {hasNested && renderSubFolders(sf?.subFolders, level + 1)}
                    </div>
                );
            })}
        </div>
    );
};

const ModuleView = ({ moduleId, onCancel }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [moduleData, setModuleData] = useState(null);
    const [selectedObjective, setSelectedObjective] = useState("All");
    const [selectedSubscription, setSelectedSubscription] = useState("All");

    useEffect(() => {
        const fetchModuleData = async () => {
            setIsLoading(true);
            try {
                const response = await getRequest(`/modules/temp-modules/${moduleId}`);
                if (response?.code === 200) {
                    setModuleData(response.body || response.body?.module || null);
                } else {
                    showMessage(response?.message || "Failed to load module details");
                }
            } catch (error) {
                showMessage("Failed to load module details");
            } finally {
                setIsLoading(false);
            }
        };

        if (moduleId) {
            fetchModuleData();
        } else {
            setIsLoading(false);
        }
    }, [moduleId]);

    const sections = moduleData?.sections || [];

    const objectiveOptions = useMemo(() => {
        const uniqueObjectives = Array.from(new Set(sections.map((s) => s?.companyObjective).filter(Boolean)));
        return ["All", ...uniqueObjectives];
    }, [sections]);

    const subscriptionOptions = useMemo(() => {
        const uniqueSubscriptions = Array.from(new Set(sections.map((s) => s?.subscriptionTier).filter(Boolean)));
        return ["All", ...uniqueSubscriptions];
    }, [sections]);

    const filteredSections = useMemo(() => {
        return sections.filter((section) => {
            const sectionObjective = section?.companyObjective || "All";
            const sectionSubscription = section?.subscriptionTier || "All";

            const objectiveMatches =
                selectedObjective === "All" || sectionObjective === "All" || sectionObjective === selectedObjective;
            const subscriptionMatches =
                selectedSubscription === "All" || sectionSubscription === "All" || sectionSubscription === selectedSubscription;

            return objectiveMatches && subscriptionMatches;
        });
    }, [sections, selectedObjective, selectedSubscription]);

    const hasActiveFilter = selectedObjective !== "All" || selectedSubscription !== "All";
    const sectionsToDisplay = hasActiveFilter ? filteredSections : [];
    const groupedSections = useMemo(() => {
        const groups = sectionsToDisplay.reduce((acc, section) => {
            const objective = section?.companyObjective || "All";
            const subscription = section?.subscriptionTier || "All";
            const key = `${objective}__${subscription}`;

            if (!acc[key]) {
                acc[key] = {
                    key,
                    objective,
                    subscription,
                    sections: []
                };
            }

            acc[key].sections.push(section);
            return acc;
        }, {});

        return Object.values(groups).sort((a, b) => {
            if (a.objective !== b.objective) return a.objective.localeCompare(b.objective);
            return a.subscription.localeCompare(b.subscription);
        });
    }, [sectionsToDisplay]);

    const totalQuestionCount = useMemo(
        () => sectionsToDisplay.reduce((acc, section) => acc + countQuestionsInSection(section), 0),
        [sectionsToDisplay]
    );

    if (isLoading) {
        return (
            <div className="df jcc aic p4rem">
                <div className="loader"></div>
                <span className="ml1rem fs1rem c15">Loading module details...</span>
            </div>
        );
    }

    if (!moduleData) {
        return (
            <div className="df fdc aic jcc p4rem">
                <p className="fs1-25rem c15 fw500 mb1rem">Module not found</p>
                <button onClick={onCancel} className="bg1 bo4 bss bw1px br5px p1rem fw600 c4 td250 cp">
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="modules-container" style={{ padding: "2rem" }}>
            <div
                className="br10px p1-5rem"
                style={{
                    background: "linear-gradient(135deg, #f9fbff 0%, #eef3ff 100%)",
                    border: "1px solid #dde6ff",
                    boxShadow: "0 10px 26px rgba(74, 103, 255, 0.14)"
                }}
            >
                <div className="df jcsb aic" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
                    <div>
                        <h1 className="modules-title m0" style={{ fontSize: "1.65rem" }}>
                            {moduleData.moduleName || "Module Details"}
                        </h1>
                        <p className="modules-subtitle m0 mt0-25rem">
                            Complete module view with sections and questions
                        </p>
                    </div>
                    <button
                        onClick={onCancel}
                        className="modules-create-btn df aic jcc cp"
                        style={{ minWidth: "140px", background: "#fff", color: "#4a67ff", border: "1px solid #ccd7ff" }}
                    >
                        <i className="fa fa-arrow-left mr0-5rem" /> Back to list
                    </button>
                </div>

                <div className="dg gg0-75rem mt1rem" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
                    <div style={statCardStyle}>
                        <div className="fs0-75rem fw600 c15 op70">Module Key</div>
                        <div className="fs0-95rem fw600 c15 mt0-25rem">{moduleData.moduleKey || "-"}</div>
                    </div>
                    <div style={statCardStyle}>
                        <div className="fs0-75rem fw600 c15 op70">Status</div>
                        <div className="fs0-95rem fw600 mt0-25rem" style={{ color: moduleData.isActive ? "#2e7d32" : "#c62828" }}>
                            {moduleData.isActive ? "Active" : "Inactive"}
                        </div>
                    </div>
                    <div style={statCardStyle}>
                        <div className="fs0-75rem fw600 c15 op70">Sections</div>
                        <div className="fs0-95rem fw600 c15 mt0-25rem">{sectionsToDisplay.length}</div>
                    </div>
                    <div style={statCardStyle}>
                        <div className="fs0-75rem fw600 c15 op70">Questions</div>
                        <div className="fs0-95rem fw600 c15 mt0-25rem">{totalQuestionCount}</div>
                    </div>
                    <div style={statCardStyle}>
                        <div className="fs0-75rem fw600 c15 op70">Created</div>
                        <div className="fs0-95rem fw600 c15 mt0-25rem">
                            {moduleData.createdAt ? moment(moduleData.createdAt).format("MMM DD, YYYY") : "-"}
                        </div>
                    </div>
                </div>

                <p className="c15 fs0-9rem mt1rem mb0" style={{ lineHeight: 1.55 }}>
                    {moduleData.moduleDescription || "No description available."}
                </p>
            </div>

            <div className="modules-filters-card mt1rem">
                <div className="df jcsb aic w100" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
                    <h3 className="fs1-1rem fw600 c15 m0">Filter by Objective and Subscription</h3>
                </div>

                <div className="w100 df aic jcsb">
                    <div className="df w100" >
                        <div className="df" style={{ gap: "0.35rem" }}>
                            <span className="fs0-75rem fw600 c15 op70 mr1rem">Objective</span>
                            <select
                                className="modules-filter-select"
                                value={selectedObjective}
                                onChange={(e) => setSelectedObjective(e.target.value)}
                                style={{ width: "100%" }}
                            >
                                {objectiveOptions.map((name) => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="df" style={{ gap: "0.35rem" }}>
                            <span className="fs0-75rem fw600 c15 op70 mx1rem">Subscription</span>
                            <select
                                className="modules-filter-select"
                                value={selectedSubscription}
                                onChange={(e) => setSelectedSubscription(e.target.value)}
                                style={{ width: "100%" }}
                            >
                                {subscriptionOptions.map((name) => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="cp bss bw1px br5px bg1 c15 fw600 fs0-8rem"
                        style={{ padding: "0.45rem 0.75rem", borderColor: "#d9e1ff" }}
                        onClick={() => {
                            setSelectedObjective("All");
                            setSelectedSubscription("All");
                        }}
                    >
                        Reset
                    </button>
                </div>

            </div>

            <div className="modules-table-card mt1rem">
                <h3 className="fs1-1rem fw600 c15 m0 mb1rem">
                    Sections ({sectionsToDisplay.length})
                </h3>

                {!hasActiveFilter && (
                    <p className="c15 fs0-9rem op70 m0">
                        Select at least one filter (Objective or Subscription) to view questions.
                    </p>
                )}

                {hasActiveFilter && sectionsToDisplay.length === 0 && (
                    <p className="c15 fs0-9rem op70 m0">No sections for the selected objective/subscription.</p>
                )}

                <div className="df fdc" style={{ gap: "1rem" }}>
                    {groupedSections.map((group) => {
                        const groupQuestionCount = group.sections.reduce((acc, section) => acc + countQuestionsInSection(section), 0);
                        return (
                            <div
                                key={group.key}
                                className="br10px p1rem"
                                style={{
                                    background: "linear-gradient(180deg, #fbfcff 0%, #f2f6ff 100%)",
                                    border: "1px solid #dfe8ff",
                                    boxShadow: "0 6px 16px rgba(74, 103, 255, 0.1)"
                                }}
                            >
                                <div
                                    className="df jcsb aic"
                                    style={{ gap: "0.75rem", flexWrap: "wrap", paddingBottom: "0.75rem", borderBottom: "1px solid #e5ecff" }}
                                >
                                    <div>
                                        <div className="fw600 c15 fs1rem">
                                            {group.objective} + {group.subscription}
                                        </div>
                                        <div className="fs0-75rem c15 op70 mt0-25rem">
                                            Sections: {group.sections.length} | Questions: {groupQuestionCount}
                                        </div>
                                    </div>
                                    <div className="df aic" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
                                        <span style={badgeStyle(OBJECTIVE_STYLES[group.objective])}>{group.objective}</span>
                                        <span style={badgeStyle(SUBSCRIPTION_STYLES[group.subscription])}>{group.subscription}</span>
                                    </div>
                                </div>

                                <div className="df fdc" style={{ gap: "0.85rem", marginTop: "0.85rem" }}>
                                    {group.sections.map((section) => (
                                        <div
                                            key={section._id || section.sectionNo}
                                            className="br10px p1rem"
                                            style={{
                                                background: "linear-gradient(180deg, #ffffff 0%, #f8faff 100%)",
                                                border: "1px solid #e7edff",
                                                boxShadow: "0 4px 12px rgba(74, 103, 255, 0.08)"
                                            }}
                                        >
                                            <div className="df jcsb aic" style={{ gap: "0.75rem", flexWrap: "wrap", paddingBottom: "0.65rem", borderBottom: "1px solid #edf1ff" }}>
                                                <div>
                                                    <div className="fw600 c15 fs0-95rem">{section.sectionNo || "-"} - {section.sectionName || "-"}</div>
                                                    <div className="fs0-75rem c15 op70 mt0-25rem">
                                                        Questions: {countQuestionsInSection(section)}
                                                    </div>
                                                </div>
                                                <div className="df aic" style={{ gap: "0.4rem", flexWrap: "wrap" }}>
                                                    <span style={badgeStyle(OBJECTIVE_STYLES[section.companyObjective])}>
                                                        {section.companyObjective || "All"}
                                                    </span>
                                                    <span style={badgeStyle(SUBSCRIPTION_STYLES[section.subscriptionTier])}>
                                                        {section.subscriptionTier || "All"}
                                                    </span>
                                                </div>
                                            </div>

                                            <div style={{ marginTop: "0.35rem" }}>
                                                {renderQuestionCard(section.question, section.sectionNo)}
                                                {renderSubFolders(section.subFolders)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ModuleView;
